import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from '@azure/msal-browser';
import { msalConfig, loginRequest } from './msalConfig';

export const msalInstance = new PublicClientApplication(msalConfig);

let initPromise = null;
let isInitialized = false;

export const ensureMsalInitialized = async () => {
  if (isInitialized) return msalInstance;

  if (!initPromise) {
    initPromise = msalInstance.initialize()
      .then(async () => {
        isInitialized = true;
        console.log('[MSAL] Initialized');
        try {
          const response = await msalInstance.handleRedirectPromise();
          if (response) {
            console.log('[MSAL] Redirect login completed:', response.account?.username);
            msalInstance.setActiveAccount(response.account);
          }
        } catch (err) {
          console.error('[MSAL] Redirect handling failed:', err);
        }
      })
      .catch((err) => {
        console.error('[MSAL] Initialization failed:', err);
        initPromise = null;
        throw err;
      });
  }

  await initPromise;
  return msalInstance;
};

export const getActiveAccount = () => {
  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) return activeAccount;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }
  return null;
};

export const isAuthenticated = () => getActiveAccount() !== null;

export const getCurrentUserEmail = () => {
  const account = getActiveAccount();
  return account?.username || null;
};

export const getCurrentUserName = () => {
  const account = getActiveAccount();
  return account?.name || account?.username || null;
};

export const acquireToken = async (scopes, forceRefresh = false) => {
  await ensureMsalInitialized();
  const account = getActiveAccount();
  if (!account) throw new Error('No authenticated account. Please sign in.');

  const request = { scopes, account, forceRefresh };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await msalInstance.acquireTokenPopup(request);
        return response.accessToken;
      } catch (popupError) {
        if (popupError instanceof BrowserAuthError && popupError.errorCode === 'popup_window_error') {
          await msalInstance.acquireTokenRedirect(request);
          return null;
        }
        throw popupError;
      }
    }
    throw error;
  }
};

export const signIn = async (useRedirect = false) => {
  await ensureMsalInitialized();
  try {
    if (useRedirect) {
      await msalInstance.loginRedirect(loginRequest);
      return null;
    } else {
      const response = await msalInstance.loginPopup(loginRequest);
      msalInstance.setActiveAccount(response.account);
      return response;
    }
  } catch (error) {
    if (error instanceof BrowserAuthError && error.errorCode === 'popup_window_error') {
      await msalInstance.loginRedirect(loginRequest);
      return null;
    }
    throw error;
  }
};

export const signOut = async () => {
  await ensureMsalInitialized();
  const account = getActiveAccount();
  if (!account) return;
  await msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
  });
};

export const clearCache = async () => {
  await ensureMsalInitialized();
  msalInstance.setActiveAccount(null);
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes('login.windows.net') || key.includes('microsoftonline'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));
};

ensureMsalInitialized().catch(console.error);

export default msalInstance;
